import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import HSeparator from "../components/HSeparator";
import TitleBar from "../components/TitleBar/TitleBar";
import EquipmentSlot, {
    SlotOption,
} from "../components/HunterFashion/EquipmentSlot";
import ImageLayoutEven from "../components/HunterFashion/ImageLayouts/ImageLayoutEven";
import ImageLayoutWide4 from "../components/HunterFashion/ImageLayouts/ImageLayoutWide4";
import ImageLayoutWide2 from "../components/HunterFashion/ImageLayouts/ImageLayoutWide2";
import ImageLayoutCarouselLeft from "../components/HunterFashion/ImageLayouts/ImageLayoutCarouselLeft";
import ImageLayoutTrio from "../components/HunterFashion/ImageLayouts/ImageLayoutTrio";
import ImageLayoutCarouselCenter from "../components/HunterFashion/ImageLayouts/ImageLayoutCarouselCenter";

import layoutCarouselCenterSvg from "../assets/layout_carousel_center.svg";
import layoutCarouselLeftSvg from "../assets/layout_carousel_left.svg";
import layoutEvenSvg from "../assets/layout_even.svg";
import layoutTrioSvg from "../assets/layout_trio.svg";
import layoutWide2Svg from "../assets/layout_wide_2.svg";
import layoutWide4Svg from "../assets/layout_wide_4.svg";
import {
    MonsterHunterGame,
    MonsterHunterSlot,
    LayoutStyle,
    HunterOutfit,
} from "@mythic/coms/types";

import styles from "./HunterFashionEditor.module.css";
import SwatchPicker from "../components/SwatchPicker";
import CreatableSelect from "react-select/creatable";

import { ActionMeta, InputActionMeta } from "react-select";
import {
    getContrastColor,
    universalSelectStyles,
} from "../components/SelectStyle";

type OutfitData = {
    weapon: number;
    head: {
        id: number;
        bodyType: number;
        primaryColor: string;
        secondaryColor: string;
    };
    chest: {
        id: number;
        bodyType: number;
        primaryColor: string;
        secondaryColor: string;
    };
    arms: {
        id: number;
        bodyType: number;
        primaryColor: string;
        secondaryColor: string;
    };
    waist: {
        id: number;
        bodyType: number;
        primaryColor: string;
        secondaryColor: string;
    };
    legs: {
        id: number;
        bodyType: number;
        primaryColor: string;
        secondaryColor: string;
    };
};

const DEFAULT_EQUIPMENT_DATA: OutfitData = Object.freeze({
    weapon: 0,
    head: {
        id: 0,
        bodyType: 1,
        primaryColor: "#000",
        secondaryColor: "#000",
    },
    chest: {
        id: 0,
        bodyType: 1,
        primaryColor: "#000",
        secondaryColor: "#000",
    },
    arms: {
        id: 0,
        bodyType: 1,
        primaryColor: "#000",
        secondaryColor: "#000",
    },
    waist: {
        id: 0,
        bodyType: 1,
        primaryColor: "#000",
        secondaryColor: "#000",
    },
    legs: {
        id: 0,
        bodyType: 1,
        primaryColor: "#000",
        secondaryColor: "#000",
    },
});

export default function HunterFashionEditor() {
    const navigate = useNavigate();
    const { outfitId } = useParams();

    const [accentColor, setAccentColor] = useState<string>("#bc58eb");
    const [layoutStyle, setLayoutStyle] = useState<LayoutStyle>("even");
    const [images, setImages] = useState<{ [key: string]: File | string }>({});
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [tagDb, setTagDb] = useState<{ value: string; label: string }[]>([]);
    const [selectedTags, setSelectedTags] = useState<
        { value: string; label: string }[]
    >([]);

    const isBusyRef = useRef(false);

    const isEditMode = Boolean(outfitId);

    const [armorDb, setArmorDb] = useState<
        (SlotOption & {
            slotType: MonsterHunterSlot;
            game: MonsterHunterGame;
            dyeable: boolean;
        })[]
    >([]);
    const [weaponDb, setWeaponDb] = useState<
        (SlotOption & { game: MonsterHunterGame })[]
    >([]);

    const [outfitData, setOutfitData] = useState<OutfitData>(
        DEFAULT_EQUIPMENT_DATA,
    );

    const handleLayoutChange = (layout: LayoutStyle) => {
        setLayoutStyle(layout);
        setImages({});
    };

    const handleImageChange = (key: string | number, image: File | null) => {
        setImages((prev) => {
            const newImages = { ...prev };
            if (image) newImages[key] = image;
            else delete newImages[key];

            return newImages;
        });
    };

    const handleColorChange = (
        key: keyof OutfitData,
        color: string,
        value: string,
    ) => {
        if (/^#[0-9A-Fa-f]{1,8}$/g.test(value))
            setOutfitData((prev) => {
                if (key === "weapon") return prev;

                return {
                    ...prev,
                    [key]: {
                        ...prev[key],
                        [color]: value,
                    },
                };
            });
    };

    const handleValueChange = (key: keyof OutfitData, newValue: any): void => {
        if (key === "weapon")
            setOutfitData((prev) => {
                const data = {
                    ...prev,
                    weapon: newValue.value,
                };
                return data;
            });
        else
            setOutfitData((prev) => ({
                ...prev,
                [key]: {
                    ...prev[key],
                    id: newValue.value,
                },
            }));
    };

    const handleBodyTypeChange = (
        key: keyof OutfitData,
        bodyType: number,
    ): void => {
        if (key === "weapon") return;

        setOutfitData((prev) => ({
            ...prev,
            [key]: {
                ...prev[key],
                bodyType,
            },
        }));
    };

    const handleSubmit = async () => {
        if (isBusyRef.current) return;

        if (name.length === 0) return;
        if (Object.entries(images).length === 0) return;
        if (!images.cover) return;

        const formData = new FormData();

        formData.append("description", description);
        formData.append("accentColor", accentColor);
        formData.append("name", name);
        formData.append("layout", layoutStyle);
        formData.append("outfitData", JSON.stringify(outfitData));
        formData.append("tags", JSON.stringify(selectedTags));

        if (isEditMode && outfitId) formData.append("outfitId", outfitId);

        Object.entries(images).forEach(([key, file]) => {
            if (file) {
                formData.append(`image_${key}`, file);
            }
        });

        try {
            const response = await fetch(
                `${import.meta.env.VITE_SERVER_URL}/api/hunterfashion/upload`,
                {
                    method: "POST",
                    body: formData,
                    credentials: "include",
                },
            );

            if (response.ok) {
                const result = await response.json();
                console.log("Upload successful: ", result);

                navigate(`/hunterfashion/outfit/${result.upsertedId}`);
            } else {
                console.error("Upload failed:", response.statusText);
            }
        } catch (err) {
            console.error("Error submitting outfit: ", err);
        } finally {
            isBusyRef.current = false;
        }
    };

    useEffect(() => {
        Promise.all([
            fetch("https://wilds.mhdb.io/en/armor").then((res) => res.json()),
            fetch("https://wilds.mhdb.io/en/weapons").then((res) => res.json()),
            fetch(
                `${import.meta.env.VITE_SERVER_URL}/api/hunterfashion/tags/get`,
            ).then((res) => res.json()),
        ]).then(([armorJson, weaponJson, tagsJson]) => {
            const armorData = armorJson.map((o: any) => {
                return {
                    value: o.id,
                    slotType: o.kind,
                    game: "Wilds",
                    dyeable: true,
                    label: o.name,
                };
            });

            const testSet = new Set();
            const weaponData = weaponJson
                .map((o: any) => {
                    return {
                        value: o.id,
                        label: o.name,
                    };
                })
                .filter((d: any) => {
                    if (testSet.has(d.label)) return false;

                    testSet.add(d.label);
                    return true;
                });

            setArmorDb(armorData);
            setWeaponDb(weaponData);
            setTagDb(tagsJson.tags);
        });
    }, []);

    useEffect(() => {
        if (isEditMode) {
            fetch(
                `${import.meta.env.VITE_SERVER_URL}/api/hunterfashion/${outfitId}/get`,
            )
                .then((res) => res.json())
                .then((data) => {
                    const outfit: HunterOutfit = data.outfit;
                    setName(outfit.name);
                    setDescription(outfit.description);
                    setOutfitData(outfit.equipment);
                    setLayoutStyle(outfit.layout);
                    setImages(outfit.images);
                    setAccentColor(outfit.accentColor);
                    setSelectedTags(outfit.tags);
                });
        }
    }, [outfitId, isEditMode]);

    const headOptions = useMemo(
        () => armorDb.filter((a) => a.slotType === "head"),
        [armorDb],
    );

    const chestOptions = useMemo(
        () => armorDb.filter((a) => a.slotType === "chest"),
        [armorDb],
    );

    const armsOptions = useMemo(
        () => armorDb.filter((a) => a.slotType === "arms"),
        [armorDb],
    );

    const waistOptions = useMemo(
        () => armorDb.filter((a) => a.slotType === "waist"),
        [armorDb],
    );

    const legsOptions = useMemo(
        () => armorDb.filter((a) => a.slotType === "legs"),
        [armorDb],
    );

    return (
        <>
            <TitleBar title="Upload Fashion" />
            <div
                className={styles.page}
                style={
                    {
                        "--accent-a": accentColor,
                        "--accent-a-contrast": getContrastColor(accentColor),
                    } as React.CSSProperties
                }
            >
                <div className={styles.themePicker}>
                    <SwatchPicker
                        color={accentColor}
                        onChange={(color) => setAccentColor(color)}
                        title="Theme Color"
                    />
                    <input
                        type="text"
                        placeholder="Name your set"
                        style={{ fontSize: "2em", flex: 1 }}
                        value={name}
                        onChange={(ev) => setName(ev.target.value)}
                    />
                </div>
                <HSeparator text="Images" padding="0.75rem 0 0 0" />
                <div className={styles.layoutSelector}>
                    <button
                        className={`${styles.layoutButton} ${layoutStyle === "even" ? styles.active : ""}`}
                        onClick={() => {
                            handleLayoutChange("even");
                        }}
                    >
                        <img
                            className={styles.layoutButtonImg}
                            src={layoutEvenSvg}
                        />
                    </button>
                    <button
                        className={`${styles.layoutButton} ${layoutStyle === "wide-2" ? styles.active : ""}`}
                        onClick={() => {
                            handleLayoutChange("wide-2");
                        }}
                    >
                        <img
                            className={styles.layoutButtonImg}
                            src={layoutWide2Svg}
                        />
                    </button>
                    <button
                        className={`${styles.layoutButton} ${layoutStyle === "wide-4" ? styles.active : ""}`}
                        onClick={() => {
                            handleLayoutChange("wide-4");
                        }}
                    >
                        <img
                            className={styles.layoutButtonImg}
                            src={layoutWide4Svg}
                        />
                    </button>
                    <button
                        className={`${styles.layoutButton} ${layoutStyle === "carousel-left" ? styles.active : ""}`}
                        onClick={() => {
                            handleLayoutChange("carousel-left");
                        }}
                    >
                        <img
                            className={styles.layoutButtonImg}
                            src={layoutCarouselLeftSvg}
                        />
                    </button>
                    <button
                        className={`${styles.layoutButton} ${layoutStyle === "trio" ? styles.active : ""}`}
                        onClick={() => {
                            handleLayoutChange("trio");
                        }}
                    >
                        <img
                            className={styles.layoutButtonImg}
                            src={layoutTrioSvg}
                        />
                    </button>
                    <button
                        className={`${styles.layoutButton} ${layoutStyle === "carousel-center" ? styles.active : ""}`}
                        onClick={() => {
                            handleLayoutChange("carousel-center");
                        }}
                    >
                        <img
                            className={styles.layoutButtonImg}
                            src={layoutCarouselCenterSvg}
                        />
                    </button>
                </div>
                {layoutStyle === "even" && (
                    <ImageLayoutEven
                        images={images}
                        onImageChange={handleImageChange}
                        editing
                    />
                )}
                {layoutStyle === "wide-4" && (
                    <ImageLayoutWide4
                        images={images}
                        onImageChange={handleImageChange}
                        editing
                    />
                )}
                {layoutStyle === "wide-2" && (
                    <ImageLayoutWide2
                        images={images}
                        onImageChange={handleImageChange}
                        editing
                    />
                )}
                {layoutStyle === "carousel-left" && (
                    <ImageLayoutCarouselLeft
                        images={images}
                        onImageChange={handleImageChange}
                        editing
                    />
                )}
                {layoutStyle === "trio" && (
                    <ImageLayoutTrio
                        images={images}
                        onImageChange={handleImageChange}
                        editing
                    />
                )}
                {layoutStyle === "carousel-center" && (
                    <ImageLayoutCarouselCenter
                        images={images}
                        onImageChange={handleImageChange}
                        editing
                    />
                )}

                <HSeparator text="Tell Us About It" padding="0.75rem 0 0 0" />
                <textarea
                    value={description}
                    onChange={(ev) => setDescription(ev.target.value)}
                    style={{
                        resize: "none",
                        height: "8rem",
                        fontSize: "2em",
                    }}
                    placeholder="Describe your outfit"
                />

                <HSeparator text="Tags" padding="0.75rem 0 0 0" />
                <CreatableSelect
                    styles={universalSelectStyles}
                    options={tagDb}
                    placeholder="Select or Create Tags"
                    value={selectedTags}
                    onCreateOption={(val) => {
                        const newOption = {
                            value: val
                                .replace(/[^0-9A-Za-z_-\s]/g, "")
                                .replace(/\s+/g, "_")
                                .toLowerCase(),
                            label: val,
                        };
                        setTagDb((prev) => [...prev, newOption]);
                        setSelectedTags((prev) => [...prev, newOption]);
                        console.log("create", val);
                    }}
                    onChange={(val, action) => {
                        console.log(val);
                        console.log(action);
                        setSelectedTags(val as any);
                    }}
                    isClearable
                    isMulti
                />

                <HSeparator text="Equipment" padding="0.75rem 0 0 0" />
                <div className={styles.equipmentGrid}>
                    <EquipmentSlot
                        options={weaponDb}
                        backLabel="WEAPON"
                        icon={`${import.meta.env.VITE_SERVER_URL}/images/MonsterHunter/Armor/Great_Sword_Icon_White.webp`}
                        value={outfitData.weapon}
                        onValueChange={(newValue) =>
                            handleValueChange("weapon", newValue)
                        }
                        canClear
                        editing
                    />
                    <EquipmentSlot
                        options={headOptions}
                        backLabel="HEAD"
                        icon={`${import.meta.env.VITE_SERVER_URL}/images/MonsterHunter/Armor/Helmet_Icon_White.webp`}
                        value={outfitData.head?.id}
                        bodyType={outfitData.head?.bodyType}
                        onBodyTypeChange={(bodyType) => {
                            handleBodyTypeChange("head", bodyType);
                        }}
                        onValueChange={(newValue) =>
                            handleValueChange("head", newValue)
                        }
                        primaryColor={outfitData.head?.primaryColor}
                        secondaryColor={outfitData.head?.secondaryColor}
                        onPrimaryColorChange={(newColor: string) =>
                            handleColorChange("head", "primaryColor", newColor)
                        }
                        onSecondaryColorChange={(newColor: string) =>
                            handleColorChange(
                                "head",
                                "secondaryColor",
                                newColor,
                            )
                        }
                        canColor
                        canClear
                        editing
                    />
                    <EquipmentSlot
                        options={chestOptions}
                        backLabel="CHEST"
                        icon={`${import.meta.env.VITE_SERVER_URL}/images/MonsterHunter/Armor/Chest_Icon_White.webp`}
                        value={outfitData.chest?.id}
                        bodyType={outfitData.chest?.bodyType}
                        onValueChange={(newValue) =>
                            handleValueChange("chest", newValue)
                        }
                        primaryColor={outfitData.chest?.primaryColor}
                        secondaryColor={outfitData.chest?.secondaryColor}
                        onPrimaryColorChange={(newColor: string) =>
                            handleColorChange("chest", "primaryColor", newColor)
                        }
                        onSecondaryColorChange={(newColor: string) =>
                            handleColorChange(
                                "chest",
                                "secondaryColor",
                                newColor,
                            )
                        }
                        onBodyTypeChange={(bodyType) => {
                            handleBodyTypeChange("chest", bodyType);
                        }}
                        canColor
                        canClear
                        editing
                    />
                    <EquipmentSlot
                        options={armsOptions}
                        backLabel="ARMS"
                        icon={`${import.meta.env.VITE_SERVER_URL}/images/MonsterHunter/Armor/Arm_Icon_White.webp`}
                        value={outfitData.arms?.id}
                        onValueChange={(newValue) =>
                            handleValueChange("arms", newValue)
                        }
                        primaryColor={outfitData.arms?.primaryColor}
                        secondaryColor={outfitData.arms?.secondaryColor}
                        onPrimaryColorChange={(newColor: string) =>
                            handleColorChange("arms", "primaryColor", newColor)
                        }
                        onSecondaryColorChange={(newColor: string) =>
                            handleColorChange(
                                "arms",
                                "secondaryColor",
                                newColor,
                            )
                        }
                        bodyType={outfitData.arms?.bodyType}
                        onBodyTypeChange={(bodyType) => {
                            handleBodyTypeChange("arms", bodyType);
                        }}
                        canColor
                        canClear
                        editing
                    />
                    <EquipmentSlot
                        options={waistOptions}
                        backLabel="WAIST"
                        icon={`${import.meta.env.VITE_SERVER_URL}/images/MonsterHunter/Armor/Waist_Icon_White.webp`}
                        value={outfitData.waist?.id}
                        onValueChange={(newValue) =>
                            handleValueChange("waist", newValue)
                        }
                        primaryColor={outfitData.waist?.primaryColor}
                        secondaryColor={outfitData.waist?.secondaryColor}
                        onPrimaryColorChange={(newColor: string) =>
                            handleColorChange("waist", "primaryColor", newColor)
                        }
                        onSecondaryColorChange={(newColor: string) =>
                            handleColorChange(
                                "waist",
                                "secondaryColor",
                                newColor,
                            )
                        }
                        bodyType={outfitData.waist?.bodyType}
                        onBodyTypeChange={(bodyType) => {
                            handleBodyTypeChange("waist", bodyType);
                        }}
                        canColor
                        canClear
                        editing
                    />
                    <EquipmentSlot
                        options={legsOptions}
                        backLabel="LEGS"
                        icon={`${import.meta.env.VITE_SERVER_URL}/images/MonsterHunter/Armor/Leg_Icon_White.webp`}
                        value={outfitData.legs?.id}
                        onValueChange={(newValue) =>
                            handleValueChange("legs", newValue)
                        }
                        primaryColor={outfitData.legs?.primaryColor}
                        secondaryColor={outfitData.legs?.secondaryColor}
                        onPrimaryColorChange={(newColor: string) =>
                            handleColorChange("legs", "primaryColor", newColor)
                        }
                        onSecondaryColorChange={(newColor: string) =>
                            handleColorChange(
                                "legs",
                                "secondaryColor",
                                newColor,
                            )
                        }
                        bodyType={outfitData.legs?.bodyType}
                        onBodyTypeChange={(bodyType) => {
                            handleBodyTypeChange("legs", bodyType);
                        }}
                        canColor
                        canClear
                        editing
                    />
                </div>
                <div>
                    <button onClick={handleSubmit}>Submit Outfit</button>
                </div>
                <div style={{ height: "256px" }}></div>
            </div>
        </>
    );
}
